/**
 * A customer's own pin, read in the browser with no Google lookup (lib/services/pins/: SharedPins, PlusCodes):
 * coordinates ("9.094312, 7.498937"), a Maps link (Google's, a `geo:` link) or a plus code. The pin is where the
 * customer put it, so it's exact, and it's not Google's content: it can be kept and shown anywhere.
 */

export interface Pin {
  lat: number;
  lng: number;
  kind: 'coordinates' | 'mapsLink' | 'plusCode';
  /** The place's name when a link carries one. */
  name?: string;
}

// ── Plus codes (Open Location Code, Google's open method, Apache 2.0) ──────

const ALPHABET = '23456789CFGHJMPQRVWX';
const SEPARATOR_AT = 8;
const PAIRS = 5;
const GRID_ROWS = 5;
const GRID_COLUMNS = 4;
const FULL = new RegExp(`^[${ALPHABET}]{8}\\+[${ALPHABET}]{2,7}$`, 'i');
const SHORT = new RegExp(`^[${ALPHABET}]{4,7}\\+[${ALPHABET}]{2,7}$`, 'i');

const validStart = (code: string) => ALPHABET.indexOf(code[0]) * 20 <= 180 && ALPHABET.indexOf(code[1]) * 20 < 360;
export const isFullCode = (code: string) => FULL.test(code) && validStart(code.toUpperCase());
export const isShortCode = (code: string) => !FULL.test(code) && SHORT.test(code) && code.indexOf('+') % 2 === 0;

/** The full code for a point (10 digits, about 14 × 14 m). */
export function encode(lat: number, lng: number): string {
  let la = Math.min(Math.max(lat, -90), 90 - 1e-10) + 90;
  let ln = (((lng + 180) % 360) + 360) % 360;
  let out = '';
  let res = 20;
  for (let i = 0; i < PAIRS; i++) {
    const a = Math.min(19, Math.max(0, Math.floor(la / res)));
    const b = Math.min(19, Math.max(0, Math.floor(ln / res)));
    out += ALPHABET[a] + ALPHABET[b];
    la -= a * res;
    ln -= b * res;
    res /= 20;
    if (out.length === SEPARATOR_AT) out += '+';
  }
  return out;
}

/** The middle of a full code's square. */
export function decode(code: string): { lat: number; lng: number } {
  const c = code.toUpperCase().replace('+', '');
  let lat = -90;
  let lng = -180;
  let res = 20;
  const pairDigits = Math.min(c.length, PAIRS * 2);
  for (let i = 0; i < pairDigits; i += 2) {
    lat += ALPHABET.indexOf(c[i]) * res;
    lng += ALPHABET.indexOf(c[i + 1]) * res;
    if (i + 2 < pairDigits) res /= 20;
  }
  let latRes = res;
  let lngRes = res;
  for (let i = PAIRS * 2; i < c.length; i++) {
    const k = ALPHABET.indexOf(c[i]);
    latRes /= GRID_ROWS;
    lngRes /= GRID_COLUMNS;
    lat += Math.floor(k / GRID_COLUMNS) * latRes;
    lng += (k % GRID_COLUMNS) * lngRes;
  }
  return { lat: lat + latRes / 2, lng: lng + lngRes / 2 };
}

/** A short code ("3FVX+PH") read as the square of that name nearest a place (right within about 50 km of it). */
export function recover(short: string, near: { lat: number; lng: number }): { lat: number; lng: number } {
  const code = short.toUpperCase();
  const padding = SEPARATOR_AT - code.indexOf('+');
  const res = Math.pow(20, 2 - padding / 2);
  const half = res / 2;
  const p = decode(encode(near.lat, near.lng).slice(0, padding) + code);
  let { lat, lng } = p;
  if (near.lat + half < lat && lat - res >= -90) lat -= res;
  else if (near.lat - half > lat && lat + res <= 90) lat += res;
  if (near.lng + half < lng) lng -= res;
  else if (near.lng - half > lng) lng += res;
  return { lat, lng };
}

/** The code people use for a point: its full code without the first four digits ("3FVX+PH"). */
export const shortCode = (lat: number, lng: number) => encode(lat, lng).slice(4);

// ── Pins in what's pasted ────────────────────────────────────────────────

const NUM = '[-+]?\\d{1,3}\\.\\d+';
const SEP = '\\s*(?:,|%2C|\\s)\\s*';
const PLACE_DATA = /!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/;
const QUERY = new RegExp(`[?&](?:q|query|ll|sll|daddr|destination|center|coordinate|loc)=(?:loc:)?(${NUM})${SEP}(${NUM})`, 'i');
const AT = new RegExp(`@(${NUM}),(${NUM})`);
const GEO = new RegExp(`geo:(${NUM}),(${NUM})`, 'i');
const PLACE_NAME = /\/maps\/place\/([^/@?]+)/;
const COORDS = new RegExp(`^\\s*(${NUM})${SEP}(${NUM})\\s*$`);
const CODE_IN = new RegExp(`([${ALPHABET}]{4,8}\\+[${ALPHABET}]{2,7})`, 'i');

const ok = (lat: number, lng: number) => Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);

/** A short link (maps.app.goo.gl, goo.gl/maps): the browser can't follow it, so it has to be opened first. */
export const isShortLink = (text: string) => /(?:maps\.app\.goo\.gl|goo\.gl\/maps)\//i.test(text);

/**
 * The pin in [text], if there is one: coordinates, a Maps link that carries its position, a `geo:` link, or a plus
 * code (a short one read against [near], the shop). Null when there's no pin to read.
 */
export function readPin(text: string, near?: { lat: number; lng: number } | null): Pin | null {
  const t = text.trim();
  if (!t) return null;
  const c = COORDS.exec(t);
  if (c && ok(+c[1], +c[2])) return { lat: +c[1], lng: +c[2], kind: 'coordinates' };
  for (const re of [PLACE_DATA, QUERY, GEO, AT]) {
    const m = re.exec(t);
    if (m && ok(+m[1], +m[2])) {
      const name = PLACE_NAME.exec(t)?.[1];
      return { lat: +m[1], lng: +m[2], kind: re === GEO ? 'coordinates' : 'mapsLink', name: name ? decodeURIComponent(name.replace(/\+/g, ' ')) : undefined };
    }
  }
  const code = CODE_IN.exec(t)?.[1];
  if (code) {
    if (isFullCode(code)) return { ...decode(code), kind: 'plusCode' };
    if (isShortCode(code) && near) return { ...recover(code, near), kind: 'plusCode' };
  }
  return null;
}

/** What a stop from a pin is called with no address given: "Shared location · 3FVX+PH". */
export const pinLabel = (p: Pin) => `${p.name ?? 'Shared location'} · ${shortCode(p.lat, p.lng)}`;

/** Where routes start, from the owner's own pin: named by its full code, as the phone names it (BusinessPlace.pinned). */
export const placePinLabel = (p: { lat: number; lng: number }) => `Pin · ${encode(p.lat, p.lng)}`;
